import { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { GetStaticPropsContext } from 'next/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import ShowroomsSection from '@features/showrooms/sections/ShowroomsSection/ShowroomsSection';
import { ENavTransparency, INavParams } from '@features/navigation/typescript';
import { getLayoutData } from '@features/layout/api';
import { getRegion } from '@utils/misc';
import { ISeo, TLocale, TRegion } from '@typescript/index';
import { TCmsBlocks, TFullImageWithHeading, IMosaicBlock } from '@features/cms/typescript';
import MainSeo from '@components/MainSeo/MainSeo';
import { REVALIDATION_TIME } from '@config/index';
import theme from '@styles/theme';
import SelfCanonicalLink from '@features/seo/SelfCanonicalLink/SelfCanonicalLink';
import { getDevProps } from '@components/DevToolbar/helpers/getDevProps';
import { getCmsDocument } from '@features/cms/services/CmsServices';
import RenderCmsBlock from '@features/cms/components/RenderCmsBlock';
type PropsType = {
  content: Record<string, TCmsBlocks> | null;
  countryCode: TRegion;
  seo: ISeo;
  showPartnersLogos: boolean;
  isBuildTime?: boolean;
};
const Showrooms = ({
  content: blocks,
  countryCode,
  seo,
  showPartnersLogos,
  isBuildTime
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  if (isBuildTime) {
    return <div>Build time</div>;
  }
  const modifyShowroomsBlock = (block: TCmsBlocks): TCmsBlocks => {
    switch (block.type) {
      case 'mosaicBlock':
        return {
          ...block,
          variant: 'other'
        } as IMosaicBlock;
      case 'fullImageWithHeading':
        return {
          ...block,
          variantAlignContent: 'end',
          contentWidth: 'large',
          buttonText: t('shoowroomMapBlocks.list'),
          buttonUrl: '/showrooms/list'
        } as TFullImageWithHeading;
      default:
        return block;
    }
  };
  return <>
      <SelfCanonicalLink />
      <main id="main-content">
        {seo && <MainSeo seo={seo} />}
        <ShowroomsSection countryCode={countryCode as string} showPartnersLogos={showPartnersLogos} />
        {blocks && Object.keys(blocks).map((key, index) => <RenderCmsBlock key={`${blocks[key].type}`} block={modifyShowroomsBlock(blocks[key])} isFirst={!index} />)}
      </main>
    </>;
};
const navParams: INavParams = {
  backgroundColor: theme.rawColors.beige300,
  fontColorVariant: 'dark',
  withBorder: false,
  navTransparency: ENavTransparency.BOTH_NOT_TRANSPARENT
};
export const getStaticProps: GetStaticProps = async ({
  locale
}: GetStaticPropsContext) => {
  if (process.env.IS_BUILD) {
    return {
      props: {
        isBuildTime: true
      },
      revalidate: 1
    };
  }
  const countryCode = getRegion('uppercase') as TRegion;
  const showPartnersLogos = process.env?.SHOW_PARTNERS_LOGOS === 'true' || false;
  const document = await getCmsDocument(locale as TLocale, {
    slug: 'showrooms'
  });
  if (!document || !document?.id) return {
    notFound: true,
    revalidate: true
  };
  return {
    props: {
      ...getDevProps('static', REVALIDATION_TIME),
      countryCode: countryCode || 'PL',
      content: await document.getAdaptedContent(),
      seo: document.seo,
      showPartnersLogos,
      isBuildTime: false,
      ...(await getLayoutData(locale as TLocale)),
      ...(await serverSideTranslations(locale as string)),
      navParams
    },
    revalidate: REVALIDATION_TIME
  };
};
export default Showrooms;